import { useState, useEffect, useRef } from 'react'

/**
 * Password/sensitive input field with visibility toggle
 * Used for passwords, API tokens, and other masked inputs
 * 
 * Security: Visibility toggle only works for user-entered values,
 * not pre-filled/autofilled passwords. Uses onKeyDown detection
 * which browsers don't trigger during autofill.
 */
const PasswordInput = ({ 
  value, 
  onChange, 
  onPaste,
  placeholder = '••••••••',
  className = '',
  error = false,
  id,
  name,
  autoComplete,
  required = false,
  disabled = false
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [userHasInteracted, setUserHasInteracted] = useState(false)
  const inputRef = useRef(null)

  // Reset if value is cleared
  useEffect(() => {
    if (!value) {
      setUserHasInteracted(false)
      setShowPassword(false)
    }
  }, [value])

  // Detect actual keystrokes (not triggered by autofill)
  const handleKeyDown = () => {
    setUserHasInteracted(true)
  }

  const handleChange = (e) => {
    // Note: some browsers trigger onChange on autofill, 
    // so we rely on onKeyDown for interaction detection
    onChange(e)
  }

  const handlePaste = (e) => {
    setUserHasInteracted(true)
    if (onPaste) onPaste(e)
  }

  // Only allow toggle if user has explicitly interacted with THIS field
  const canToggleVisibility = userHasInteracted && value

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        name={name}
        type={showPassword && canToggleVisibility ? 'text' : 'password'}
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        className={`input-field pr-12 ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''} ${className}`}
      />
      <button
        type="button"
        onClick={() => canToggleVisibility && setShowPassword(!showPassword)}
        disabled={!canToggleVisibility}
        className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-colors ${
          canToggleVisibility 
            ? 'text-slate-400 hover:text-slate-600 cursor-pointer' 
            : 'text-slate-200 cursor-not-allowed'
        }`}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {showPassword && canToggleVisibility ? (
          // Eye-off icon (password visible, click to hide)
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" 
            />
          </svg>
        ) : (
          // Eye icon (password hidden, click to show)
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
            />
          </svg>
        )}
      </button>
    </div>
  )
}

export default PasswordInput
